import React, { useEffect, useState, useContext, useMemo } from "react";
import Badge from "../../../components/Badge";
import Button from "../../../components/Button";
import Loader from "../../../components/Loader";
import H4 from "../../../components/Typography/H4";
import { useFetchApi } from "../../../hooks/useFetchApi";
import AddEditModal from "./AddEditModal";
import DeleteModal from "./DeleteModal";
import { format, parseISO } from "date-fns";
import * as XLSX from "xlsx";
import { StateContext } from "../../../store/context";
import { isAllowed } from "../../../components/Entitlements";
import { KEYS } from "../../../setup/entitlements";
import { Table } from "../../../components/table";
import type { ColumnsType } from "antd/es/table";

// Define the User type
interface User {
  id: number;
  email: string;
  mobile_number: string;
  full_name: string;
  status: boolean;
  sub_plan: string;
  sub_start_date?: string;
  sub_end_date?: string;
  role: string;
  coupon_code?: string;
  last_login_at?: string;
}

// Define the Role type
interface Role {
  id: number;
  name: string;
  status: number;
}

const Users: React.FC = (): React.ReactElement => {
  const {
    authUser: { role_name, allowed = [] },
  } = useContext(StateContext);

  const isAdmin: boolean = role_name === "admin";

  const [selectedData, setEditData] = useState<any>({});
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedRole, setSelectedRole] = useState<string>("");
  const [selectedCoupon, setSelectedCoupon] = useState<string>("all");
  const [selectedLastLogin, setSelectedLastLogin] = useState<string>("all");
  const [openModal, toggleModal] = useState<any>({
    newEdit: false,
    delete: false,
  });

  const {
    execute: getRoles,
    loading: roleLoading,
    response: roles,
  } = useFetchApi("/api/roles", {}, "GET");

  console.log("roles: ", roles);

  const {
    loading,
    execute,
    response: users,
  } = useFetchApi("/api/users", {}, "GET");

  useEffect(() => {
    execute();
    getRoles();
  }, []);

  const toggle = (e: any, user?: any) => {
    const type = e.target.id;
    console.log("type", type);
    if (user && Object.keys(user).length) {
      setEditData(user);
    } else {
      setEditData({});
    }
    if (type) {
      toggleModal({ ...openModal, [type]: !openModal[type] });
    }
    if (openModal[type]) {
      setEditData({});
    }
  };

  const filteredRoles = useMemo(() => {
    if (!roles) return [];

    if (isAdmin) {
      return roles.filter((role: Role) => role.status === 1);
    } else {
      // For non-admin users, only show Basic Customer and Premium Customer
      return roles.filter(
        (role: Role) =>
          role.status === 1 &&
          (role.name === "Basic Customer" || role.name === "Premium Customer")
      );
    }
  }, [roles, isAdmin]);

  const filteredUsers = useMemo(() => {
    if (!users) {
      return [];
    }

    let filtered = users;

    // For non-admin users, only show Basic Customer and Premium Customer users
    if (!isAdmin) {
      filtered = filtered.filter(
        (user: User) =>
          user.role === "Basic Customer" || user.role === "Premium Customer"
      );
    }

    // Filter by search term (email) - only for admin users
    if (isAdmin && searchTerm.trim()) {
      filtered = filtered.filter((user: User) =>
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by selected role
    if (selectedRole && selectedRole !== "") {
      filtered = filtered.filter(
        (user: User) => user.role.toLowerCase() === selectedRole.toLowerCase()
      );
    }

    // Filter by coupon code
    if (selectedCoupon === "yes") {
      filtered = filtered.filter(
        (user: User) => user.coupon_code && user.coupon_code.trim() !== ""
      );
    } else if (selectedCoupon === "no") {
      filtered = filtered.filter(
        (user: User) => !user.coupon_code || user.coupon_code.trim() === ""
      );
    }

    // Filter by last login
    if (selectedLastLogin !== "all") {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const pastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      const lastMonth = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

      filtered = filtered.filter((user: User) => {
        if (!user.last_login_at) {
          return selectedLastLogin === "never";
        }

        const lastLoginDate = new Date(user.last_login_at);

        switch (selectedLastLogin) {
          case "today":
            return lastLoginDate >= today;
          case "past_week":
            return lastLoginDate >= pastWeek;
          case "last_month":
            return lastLoginDate >= lastMonth;
          case "over_month":
            return lastLoginDate < lastMonth;
          case "never":
            return false; 
          default:
            return true;
        }
      });
    }

    return filtered;
  }, [users, searchTerm, selectedRole, selectedCoupon, selectedLastLogin, isAdmin]);

  const clearSearch = () => {
    setSearchTerm("");
  };

  const clearRoleFilter = () => {
    setSelectedRole("");
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setSelectedRole("");
    setSelectedCoupon("all");
    setSelectedLastLogin("all");
  };

  const exportToExcel = () => {
    if (!users || !users.length) {
      alert("No data to export");
      return;
    }
    let exportData;
    let colWidths;

    if (isAdmin) {
      exportData = users.map((user: any) => ({
        Email: user.email,
        "Mobile Number": user.mobile_number,
        Country: user.country,
        Status: user.status ? "Active" : "Inactive",
        "Current Plan": user.sub_plan,
        "Plan Start Date": user?.sub_start_date
          ? format(parseISO(user.sub_start_date), "yyyy-MM-dd")
          : "N/A",
        "Plan End Date": user?.sub_end_date
          ? format(parseISO(user.sub_end_date), "yyyy-MM-dd")
          : "N/A",
        Role: user.role,
        "Coupon Code": user.coupon_code || "N/A",
        "Last Login At": user.last_login_at
          ? new Date(user.last_login_at)
              .toLocaleString("en-CA", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: true,
              })
              .replace(",", "")
          : "",
      }));

      colWidths = [
        { wch: 25 }, // Email
        { wch: 15 }, // Mobile Number
        { wch: 15 }, // Country
        { wch: 10 }, // Status
        { wch: 15 }, // Current Plan
        { wch: 15 }, // Plan Start Date
        { wch: 15 }, // Plan End Date
        { wch: 15 }, // Role
        { wch: 15 }, // Coupon Code
        { wch: 15 }, // Last Login At
      ];
    } else {
      exportData = users.map((user: any) => ({
        Name: user.full_name,
        Country: user.country,
        "Current Plan": user.sub_plan,
        "Coupon Code": user.coupon_code || "N/A",
        "Plan Start Date": user?.sub_start_date
          ? format(parseISO(user.sub_start_date), "yyyy-MM-dd")
          : "N/A",
        "Plan End Date": user?.sub_end_date
          ? format(parseISO(user.sub_end_date), "yyyy-MM-dd")
          : "N/A",
      }));

      colWidths = [
        { wch: 25 }, // Name
        { wch: 15 }, // Country
        { wch: 15 }, // Current Plan
        { wch: 15 }, // Coupon Code
        { wch: 15 }, // Plan Start Date
        { wch: 15 }, // Plan End Date
      ];
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    ws["!cols"] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, "Users");

    const filename = `Users Export_${format(new Date(), "yyyy-MM-dd")}.xlsx`;

    XLSX.writeFile(wb, filename);
  };

  // Define columns for admin view
  const adminColumns: ColumnsType<User> = [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      align: "left",
    },
    {
      title: "Mobile #",
      dataIndex: "mobile_number",
      key: "mobile_number",
      align: "left",
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
      align: "left",
      width: 100,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (status: boolean) => (
        <Badge status={status ? "success" : "error"}>
          {status ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      title: "Current Plan",
      dataIndex: "sub_plan",
      key: "sub_plan",
      align: "left",
    },
    {
      title: "Plan Start Date",
      dataIndex: "sub_start_date",
      key: "sub_start_date",
      align: "left",
      sorter: (a: User, b: User) => {
        const dateA = a.sub_start_date
          ? new Date(a.sub_start_date).getTime()
          : 0;
        const dateB = b.sub_start_date
          ? new Date(b.sub_start_date).getTime()
          : 0;
        return dateA - dateB;
      },
      sortDirections: ["ascend", "descend"],
      render: (date: string) =>
        date ? format(parseISO(date), "yyyy-MM-dd") : "N/A",
    },
    {
      title: "Plan End Date",
      dataIndex: "sub_end_date",
      key: "sub_end_date",
      align: "left",
      sorter: (a: User, b: User) => {
        const dateA = a.sub_end_date ? new Date(a.sub_end_date).getTime() : 0;
        const dateB = b.sub_end_date ? new Date(b.sub_end_date).getTime() : 0;
        return dateA - dateB;
      },
      sortDirections: ["ascend", "descend"],
      render: (date: string) =>
        date ? format(parseISO(date), "yyyy-MM-dd") : "N/A",
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      align: "left",
    },
    {
      title: "Coupon Code",
      dataIndex: "coupon_code",
      key: "coupon_code",
      align: "left",
    },
    {
      title: "Last Login At",
      dataIndex: "last_login_at",
      key: "last_login_at",
      align: "left",
      render: (date: string) =>
        date
          ? new Date(date)
              .toLocaleString("en-CA", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: true,
              })
              .replace(",", "")
          : "",
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 250,
      render: (_, user: User) => (
        <div className="flex gap-2 justify-center">
          <Button
            fontSize="small"
            variant="primary"
            id="newEdit"
            onClick={(e: any) => toggle(e, user)}
          >
            Edit
          </Button>
          <Button
            fontSize="small"
            variant="error"
            id="delete"
            onClick={(e: any) => toggle(e, user)}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  // Define columns for non-admin view
  const userColumns: ColumnsType<User> = [
    {
      title: "Name",
      dataIndex: "full_name",
      key: "full_name",
      align: "left",
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
      align: "left",
      width: 100,
    },
    {
      title: "Current Plan",
      dataIndex: "sub_plan",
      key: "sub_plan",
      align: "left",
    },
    {
      title: "Coupon Code",
      dataIndex: "coupon_code",
      key: "coupon_code",
      align: "left",
    },
    {
      title: "Plan Start Date",
      dataIndex: "sub_start_date",
      key: "sub_start_date",
      align: "left",
      sorter: (a: User, b: User) => {
        const dateA = a.sub_start_date
          ? new Date(a.sub_start_date).getTime()
          : 0;
        const dateB = b.sub_start_date
          ? new Date(b.sub_start_date).getTime()
          : 0;
        return dateA - dateB;
      },
      sortDirections: ["ascend", "descend"],
      render: (date: string) =>
        date ? format(parseISO(date), "yyyy-MM-dd") : "N/A",
    },
    {
      title: "Plan End Date",
      dataIndex: "sub_end_date",
      key: "sub_end_date",
      align: "left",
      sorter: (a: User, b: User) => {
        const dateA = a.sub_end_date ? new Date(a.sub_end_date).getTime() : 0;
        const dateB = b.sub_end_date ? new Date(b.sub_end_date).getTime() : 0;
        return dateA - dateB;
      },
      sortDirections: ["ascend", "descend"],
      render: (date: string) =>
        date ? format(parseISO(date), "yyyy-MM-dd") : "N/A",
    },
  ];

  const columns = isAdmin ? adminColumns : userColumns;

  return (
    <div className="flex flex-col w-full p-8 bg-white min-h-full">
      <Loader show={loading && roleLoading} />
      <AddEditModal
        roles={roles}
        data={selectedData}
        status={openModal.newEdit}
        onClose={toggle}
        onSuccess={execute}
      />
      <DeleteModal
        id="delete"
        data={selectedData}
        status={openModal.delete}
        onClose={toggle}
        onSuccess={execute}
      />

      <div className="flex justify-between mb-8 items-center relative z-0 flex-wrap gap-4">
        {/* Left side - Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Email search - only show for admin users */}
          {isAdmin && (
            <div className="relative min-w-64 max-w-md">
              <input
                type="text"
                placeholder="Search by email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}

          <div className="relative min-w-48">
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="">All Roles</option>
              {filteredRoles.map((role: Role) => (
                <option key={role.id} value={role.name}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>

          <div className="relative min-w-48">
            <select
              value={selectedCoupon}
              onChange={(e) => setSelectedCoupon(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="all">Coupons</option>
              <option value="yes">Has Coupon</option>
              <option value="no">No Coupon</option>
            </select>
          </div>

          <div className="relative min-w-48">
            <select
              value={selectedLastLogin}
              onChange={(e) => setSelectedLastLogin(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="all">Last Login</option>
              <option value="today">Today</option>
              <option value="past_week">Past Week</option>
              <option value="last_month">Last Month</option>
              <option value="over_month">Over a Month Ago</option>
              <option value="never">Never Logged In</option>
            </select>
          </div>

          <div className="flex gap-2">
            {isAdmin && searchTerm && (
              <button
                onClick={clearSearch}
                className="px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Clear Search
              </button>
            )}
            {selectedRole && (
              <button
                onClick={clearRoleFilter}
                className="px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Clear Role
              </button>
            )}
            {((isAdmin && searchTerm) || selectedRole || selectedCoupon !== "all" || selectedLastLogin !== "all") && (
              <button
                onClick={clearAllFilters}
                className="px-3 py-2 text-sm text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors"
              >
                Clear All
              </button>
            )}
          </div>

          {((isAdmin && searchTerm) || selectedRole || selectedCoupon !== "all" || selectedLastLogin !== "all") && (
            <span className="text-sm text-gray-600 whitespace-nowrap">
              Found {filteredUsers.length} of {users?.length || 0}
            </span>
          )}
        </div>

        {/* Right side - Action Buttons */}
        <div className="flex gap-2">
          {(isAdmin || isAllowed([KEYS.EXPORT_TABLE_OPTION], allowed)) && (
            <Button onClick={exportToExcel} variant="success">
              Export to Excel
            </Button>
          )}
          {isAdmin && (
            <Button id="newEdit" onClick={toggle} variant="primary">
              Add New Users
            </Button>
          )}
        </div>
      </div>

      <div className="relative z-0">
        <Table<User>
          columns={columns}
          dataSource={filteredUsers}
          rowKey="id"
          loading={loading && roleLoading}
          pagination={{
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          rowHoverable={true}
          bordered={true}
          size="middle"
          className="shadow-sm"
        />
      </div>
    </div>
  );
};

export default Users;
